// =============================================================================
// features/sales/campaigns/detail/components/PauseResumeButton.tsx — Ação de
// pausar/retomar do cabeçalho (doc 26 §6.7 "Visão geral... ações de pausar/
// retomar"). Só aparece quando o status permite a transição
// (`active`→pausar, `paused`→retomar); outros status não mostram o botão.
//
// Rigor assimétrico DELIBERADO entre as duas ações (achado de revisão do
// PR #717 — a própria aba Configuração já exigia confirmação de impacto
// para editar uma campanha `active`, mas Retomar disparava a chamada direto
// no clique com o MESMO impacto imediato — a distribuição de contatos a
// cidadãos recomeça):
//   - **Retomar** abre `ImpactConfirmDialog` (reuso do mesmo componente da
//     aba Configuração, com `message` no lugar do diff campo a campo — não
//     há "campo alterado" aqui) antes de chamar a API.
//   - **Pausar** dispara direto — é a ação SEGURA (interrompe, não inicia);
//     exigir confirmação para parar algo que está incomodando é atrito sem
//     ganho.
//
// Idempotência (DoD do slot "clique duplo não duplica"): desabilitado
// durante `isPending` (1ª linha de defesa) — a garantia real é do backend
// (`POST /pause|/resume` já no estado alvo responde 200 sem efeito
// colateral, ver apps/api/src/modules/sales/campaigns/service.ts).
// =============================================================================

import type { CampaignStatus } from '@elemento/shared-schemas';
import * as React from 'react';

import { Button } from '../../../../../components/ui/Button';
import { useToast } from '../../../../../components/ui/Toast';
import { useCampaignMembersCount } from '../../../../../hooks/sales/useCampaignDetail';
import { ApiError } from '../../../../../lib/api';

import { ImpactConfirmDialog } from './ImpactConfirmDialog';

export interface PauseResumeButtonProps {
  campaignId: string;
  campaignName: string;
  status: CampaignStatus;
  canManage: boolean;
  onPause: () => Promise<unknown>;
  onResume: () => Promise<unknown>;
  isPending: boolean;
}

export function PauseResumeButton({
  campaignId,
  campaignName,
  status,
  canManage,
  onPause,
  onResume,
  isPending,
}: PauseResumeButtonProps): React.JSX.Element | null {
  const { toast } = useToast();
  const [showResumeConfirm, setShowResumeConfirm] = React.useState(false);

  // Único número "à mão" (uma chamada real ao servidor, `total` de
  // `GET /members?status=available&limit=1` — mesmo padrão de
  // `useCampaignsCount`): quantos membros ficam imediatamente elegíveis
  // para contato assim que a campanha volta a `active`. Só busca quando o
  // diálogo de retomar pode de fato aparecer (`status === 'paused'`).
  const availableCount = useCampaignMembersCount(
    campaignId,
    { status: ['available'] },
    { enabled: canManage && status === 'paused' },
  );

  if (!canManage) return null;
  if (status !== 'active' && status !== 'paused') return null;

  async function handlePause(): Promise<void> {
    try {
      await onPause();
      toast('Campanha pausada.', 'success');
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : 'Não foi possível concluir a ação agora.';
      toast(message, 'danger');
    }
  }

  async function confirmResume(): Promise<{ ok: true } | { ok: false; message: string }> {
    try {
      await onResume();
      toast('Campanha retomada.', 'success');
      return { ok: true };
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : 'Não foi possível retomar esta campanha agora.';
      return { ok: false, message };
    }
  }

  const resumeMessage =
    availableCount.total !== undefined
      ? `${availableCount.total.toLocaleString('pt-BR')} cliente(s) ficam imediatamente disponíveis para contato — a distribuição desta campanha recomeça agora.`
      : 'A distribuição de contatos desta campanha recomeça imediatamente.';

  return (
    <>
      <Button
        type="button"
        variant={status === 'active' ? 'outline' : 'secondary'}
        size="default"
        disabled={isPending}
        onClick={() => (status === 'active' ? void handlePause() : setShowResumeConfirm(true))}
      >
        {isPending ? 'Aguarde…' : status === 'active' ? 'Pausar campanha' : 'Retomar campanha'}
      </Button>

      {showResumeConfirm && (
        <ImpactConfirmDialog
          campaignName={campaignName}
          message={resumeMessage}
          title={`Retomar “${campaignName}”?`}
          description="Esta campanha está pausada — retomar volta a distribuir e trabalhar a carteira imediatamente."
          confirmLabel="Confirmar retomada"
          pendingLabel="Retomando…"
          errorTitle="Não foi possível retomar"
          onClose={() => setShowResumeConfirm(false)}
          onConfirm={confirmResume}
        />
      )}
    </>
  );
}
